import type { VendorPricelistIntakeLineWire as Line } from '../api/types'

/**
 * One place that decides, for a price-sheet line, which review bucket it is
 * in, what its status reads as, and the plain-English reason a person sees.
 * The review page, its filters and its counts all go through here so they
 * can't disagree.
 */

export type ReviewGroup = 'ready' | 'decide' | 'new_tier' | 'not_in_odoo' | 'done'

export const GROUP_LABEL: Record<ReviewGroup, string> = {
  ready: 'Ready to apply',
  decide: 'Needs a decision',
  new_tier: 'New tiers',
  not_in_odoo: 'Not in Odoo',
  done: 'Done',
}

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

export interface LineStatusView {
  label: string
  tone: StatusTone
}

const DONE = new Set(['applied', 'scheduled', 'skipped', 'rejected'])

export function isNewTier(line: Line): boolean {
  return line.line_status === 'new' && line.matched_product_tmpl_id != null
}

/** Odoo prices are per kg/lb; a line with no stated unit can't be written. */
export function needsPriceUnit(line: Line): boolean {
  return (
    line.parsed_price_unit == null &&
    (line.line_status === 'matched' ||
      line.line_status === 'new' ||
      line.line_status === 'unmatched')
  )
}

function hasUnitMismatch(line: Line): boolean {
  return (
    line.parsed_price_unit != null &&
    line.apply_price == null &&
    Boolean(line.unit_problem) &&
    (line.line_status === 'matched' || isNewTier(line))
  )
}

/** Matched/new-tier line whose price can be put into Odoo's unit. */
export function isApplicable(line: Line): boolean {
  return (line.line_status === 'matched' || isNewTier(line)) && line.apply_price != null
}

export function reviewGroup(line: Line): ReviewGroup {
  if (DONE.has(line.line_status)) return 'done'
  if (line.line_status === 'unmatched' || line.review_reason) return 'decide'
  if (needsPriceUnit(line) || hasUnitMismatch(line)) return 'decide'
  if (line.line_status === 'matched') return 'ready'
  if (isNewTier(line)) return 'new_tier'
  return 'not_in_odoo'
}

export function lineStatusView(line: Line): LineStatusView {
  switch (line.line_status) {
    case 'applied':
      return { label: 'Applied', tone: 'success' }
    case 'scheduled':
      return { label: 'Scheduled', tone: 'info' }
    case 'skipped':
      return { label: 'Skipped', tone: 'neutral' }
    case 'rejected':
      return { label: 'Rejected', tone: 'neutral' }
  }
  if (needsPriceUnit(line)) return { label: 'Unit missing', tone: 'danger' }
  if (hasUnitMismatch(line)) return { label: 'Unit mismatch', tone: 'danger' }
  switch (line.review_reason) {
    case 'price_mismatch':
      return { label: 'Check price', tone: 'warning' }
    case 'tier_ambiguous':
    case 'shared_row':
      return { label: 'Pick Odoo row', tone: 'warning' }
    case 'several_products':
      return { label: 'Pick product', tone: 'warning' }
  }
  if (line.line_status === 'unmatched') return { label: 'Needs a decision', tone: 'warning' }
  if (line.line_status === 'matched') {
    return line.price_changed === false
      ? { label: 'No change', tone: 'neutral' }
      : { label: 'Ready', tone: 'success' }
  }
  if (isNewTier(line)) return { label: 'New tier', tone: 'info' }
  return { label: 'Not in Odoo', tone: 'neutral' }
}

export function money(value: number | null | undefined): string {
  if (value == null) return '—'
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function perUnit(unit: string | null | undefined): string {
  return unit ? `/${unit}` : ''
}

function tierText(line: Line): string {
  if (line.parsed_min_qty != null) {
    return `MOQ ${line.parsed_min_qty.toLocaleString()}${line.parsed_min_qty_unit ? ` ${line.parsed_min_qty_unit}` : ''}`
  }
  return line.parsed_packaging_raw ? `the ${line.parsed_packaging_raw} pack` : 'this pack'
}

/** The sentence under the status: why the line is where it is and what to do. */
export function lineExplanation(line: Line, vendorName: string): string | null {
  const vendor = vendorName || 'the vendor'
  const product = line.matched_product_name ?? 'this product'
  if (DONE.has(line.line_status)) return null
  if (needsPriceUnit(line)) {
    return 'The sheet doesn’t say what unit this price is per. Set kg or lb for the sheet above.'
  }
  if (hasUnitMismatch(line)) {
    if (line.matched_supplierinfo_id != null && line.matched_product_tmpl_id == null) {
      return (
        `Odoo's ${vendor} price row for this code isn't attached to any SPI product and has no unit, ` +
        `so the price can't be written to it. Map this line to the right SPI product below (it's added as ` +
        `a new price row), or skip it. The orphan row should be cleaned up in Odoo.`
      )
    }
    return `${line.unit_problem ?? 'The price unit can’t be converted'}. Fix the unit in Odoo or skip this line.`
  }
  switch (line.review_reason) {
    case 'price_mismatch':
      return (
        `Matched to ${product} by name. Odoo has ${money(line.current_price)}${perUnit(line.odoo_uom)}, ` +
        `but ${vendor} says the previous price was ${money(line.parsed_previous_price)}` +
        `${perUnit(line.parsed_price_unit)}. Confirm it's the right Odoo row before updating it.`
      )
    case 'shared_row':
      return (
        `Several lines on this sheet point at the same Odoo row for ${product}. ` +
        `Pick the Odoo row ${tierText(line)} updates, or add it as a new tier.`
      )
    case 'tier_ambiguous':
      return `Odoo has more than one price for ${product} from ${vendor}. Pick the one ${tierText(line)} replaces, or add it as a new tier.`
    case 'several_products':
      return 'More than one SPI product matches this name. Choose the right one.'
  }
  if (line.line_status === 'unmatched')
    return line.match_note ?? 'Pick the Odoo row or product this line belongs to.'
  if (isNewTier(line)) {
    return `Odoo doesn't have ${tierText(line)} for ${product} from ${vendor} yet. Apply adds it as a new price row.`
  }
  if (line.line_status === 'new') {
    return `SPI doesn't buy this product from ${vendor} in Odoo. Map it to an SPI product to add it.`
  }
  return null
}

/** A few words for the table's status column; the full sentence is in the row's panel. */
export function lineShortReason(line: Line): string | null {
  if (line.line_status === 'skipped') return line.match_note
  if (DONE.has(line.line_status)) return null
  if (needsPriceUnit(line)) return 'Sheet gives no unit'
  if (hasUnitMismatch(line)) return 'Unit can’t be converted'
  switch (line.review_reason) {
    case 'price_mismatch':
      return 'Odoo’s price isn’t the vendor’s previous price'
    case 'shared_row':
      return 'Shares an Odoo row with another line'
    case 'tier_ambiguous':
      return 'Several Odoo prices; pick one'
    case 'several_products':
      return 'Several SPI products match'
  }
  if (line.line_status === 'unmatched') return 'No Odoo row picked'
  return null
}

export function matchesSearch(line: Line, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return [
    line.vendor_product_name_raw,
    line.vendor_product_code_raw,
    line.matched_product_name,
  ].some((v) => v != null && v.toLowerCase().includes(needle))
}
